import { useMemo } from "react";
import { AutoScrollActivator } from "@dnd-kit/core";
import Tree from "@/shared/data-display/tree";
import { Kanban } from "@/shared/kanban/ui";
import { useCompanyDocumentTree } from "../model/use-company-document-tree";
import { useCompanyDocumentTreeDnd } from "../model/use-company-document-tree-dnd";
import { CompanyDocumentTreeDndContext } from "../model/company-document-tree-dnd-context";
import { TreeNodeTitle } from "../views/tree-node-title/tree-node-title";
import { TreeSwitcherIcon } from "../views/tree-switcher-icon/tree-switcher-icon";
import { treeClassName } from "../views/tree-styles";
import { EmptyTree } from "../views/empty-tree/empty-tree";
import { NothingFound } from "../views/nothing-found/nothing-found";
import { levelPanelCollision } from "../utils/level-panel-collision";
import { isHiddenNode } from "../utils/tree-node-guards";
import {
  CompanyDocumentTreeDataNode,
  CompanyDocumentTreeNode,
} from "../model/types";
import { TreeNodeDnd } from "./tree-node-dnd";
import { LevelDropPanel } from "./level-drop-panel/level-drop-panel";
import { DragPreview } from "./drag-preview";

/**
 * Прокрутка начинается, только когда указатель у самого края окна (8% высоты):
 * панель уровней стоит выше этой полосы, и прицеливание в неё страницу не двигает.
 */
const AUTO_SCROLL = {
  activator: AutoScrollActivator.Pointer,
  threshold: { x: 0, y: 0.08 },
};

export function CompanyDocumentTree({
  canManage,
  onSelectNode,
}: {
  canManage: boolean;
  onSelectNode?: (node?: CompanyDocumentTreeNode) => void;
}) {
  const {
    treeData,
    nodeById,
    searching,
    expandedKeys,
    setExpandedKeys,
    expand,
    selectedKey,
    select,
  } = useCompanyDocumentTree();
  const {
    activeAncestors,
    dropTargetKey,
    moving,
    onDragStart,
    onDragOver,
    onDragEnd,
    onDragCancel,
  } = useCompanyDocumentTreeDnd({ nodeById, expand });

  const canDrag = canManage && !searching;

  // Дерево не зависит от состояния перетаскивания: при смене цели перерисовываются
  // только строки, читающие его из контекста.
  const tree = useMemo(
    () => (
      <Tree<CompanyDocumentTreeDataNode>
        blockNode
        className={treeClassName}
        switcherIcon={(props) => (
          <TreeSwitcherIcon expanded={props.expanded} isLeaf={props.isLeaf} />
        )}
        treeData={treeData}
        expandedKeys={expandedKeys}
        onExpand={(keys) => setExpandedKeys(keys.map(String))}
        selectedKeys={selectedKey ? [selectedKey] : []}
        onSelect={(_, info) => {
          const key = String(info.node.key);
          const nextKey = selectedKey === key ? undefined : key;
          select(nextKey);
          onSelectNode?.(nextKey ? nodeById.get(nextKey) : undefined);
        }}
        titleRender={(node) => (
          <TreeNodeDnd
            node={node}
            draggable={canDrag && !isHiddenNode(node.node)}
          >
            <TreeNodeTitle
              node={node}
              canManage={canManage}
              expanded={expandedKeys.includes(String(node.key))}
            />
          </TreeNodeDnd>
        )}
      />
    ),
    [
      treeData,
      expandedKeys,
      setExpandedKeys,
      selectedKey,
      select,
      onSelectNode,
      nodeById,
      canDrag,
      canManage,
    ],
  );

  const dndState = useMemo(
    () => ({ dropTargetKey, moving }),
    [dropTargetKey, moving],
  );

  if (!treeData.length) {
    return searching ? <NothingFound /> : <EmptyTree />;
  }

  return (
    <Kanban.Context
      collisionDetection={levelPanelCollision}
      autoScroll={AUTO_SCROLL}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <CompanyDocumentTreeDndContext.Provider value={dndState}>
        {tree}
      </CompanyDocumentTreeDndContext.Provider>

      {/* Узлу из корня подниматься некуда — панель ему не нужна. */}
      {!!activeAncestors.length && (
        <LevelDropPanel
          ancestors={activeAncestors}
          dropTargetKey={dropTargetKey}
        />
      )}

      <Kanban.CopyOfDraggable>
        <DragPreview />
      </Kanban.CopyOfDraggable>
    </Kanban.Context>
  );
}
